import { Injectable } from "@angular/core";
import { HostSpecs, ROOM_LIVE_STATUS } from "@common/domain";
import {
  Actions,
  concatLatestFrom,
  createEffect,
  EffectNotification,
  ofType,
  OnRunEffects
} from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { AccountJwtService } from "@youm-front/account-jwt/data-access";
import { LicenseActions } from "@youm-front/license/data-access";
import { NoticeFacade } from "@youm-front/notice/data-access";
import { NoticeCreate1Cmd } from "@youm-front/notice/domain";
import { RoomError } from "@youm-front/room/domain";
import { ConfirmService } from "@youm-front/ui";
import ms from "ms";
import { Observable } from "rxjs";
import {
  delay,
  exhaustMap,
  filter,
  map,
  mergeMap,
  take,
  takeUntil,
  tap
} from "rxjs/operators";
import {
  RoomActions,
  RoomBrandBgActions,
  RoomBrandLogoActions,
  RoomBrandOverlayActions
} from "../actions";
import {
  RoomBrandBgEvents,
  RoomBrandCaptionLowerThirdEvents,
  RoomBrandLogoEvents,
  RoomBrandOverlayEvents,
  RoomLiveStreamStatusEvents,
  RoomMixerEvents,
  RoomStageLayoutEvents
} from "../events";
import { RoomSelectors } from "../selectors";

@Injectable()
export class RoomAlertsEffects implements OnRunEffects {
  readonly room$ = this.store.pipe(select(RoomSelectors.room));

  constructor(
    private readonly store: Store,
    private readonly _actions$: Actions,
    private readonly _noticeFacade: NoticeFacade,
    private readonly _confirm: ConfirmService,
    private readonly accountJwtService: AccountJwtService
  ) {}

  // ERROR of the live (YOUM-154): its WebRTC stream could not be produced.
  // Told to the host only; the other outputs (HLS) may still be live.
  roomLiveStreamError$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomLiveStreamStatusEvents.update),
        filter((_) => _.status === ROOM_LIVE_STATUS.ERROR),
        concatLatestFrom(() => [
          this.room$,
          this.accountJwtService.anyAccountJwtVO$
        ]),
        filter(
          ([{ roomId }, room, accountJwt]) =>
            room?.value.id === roomId &&
            HostSpecs.isHost(accountJwt, room.value.ownerId)
        ),
        tap(() =>
          this._noticeFacade.noticeCreate(
            new NoticeCreate1Cmd({
              kind: "error",
              data: $localize`:@@roomAlerts.liveStreamError:The WebRTC stream of the live could not be started: the WebRTC viewers do not see the live. The other outputs may still work.`
            })
          )
        )
      ),
    { dispatch: false }
  );

  roomBrandBgDeferredError$ = createEffect(
    () =>
      this._actions$.pipe(() =>
        this._actions$.pipe(
          ofType(RoomBrandBgEvents.roomBrandBgDeferredErrorEvent),
          tap((_) =>
            this._noticeFacade.noticeCreate(
              new NoticeCreate1Cmd({
                kind: "error",
                data: $localize`Brand background error: ${_.roomBrandBgDeferredErrorVO.toString()}`
              })
            )
          )
        )
      ),
    { dispatch: false }
  );

  liveBrandCaptionLowerThirdDeferredError$ = createEffect(
    () =>
      this._actions$.pipe(() =>
        this._actions$.pipe(
          ofType(
            RoomBrandCaptionLowerThirdEvents.roomBrandCaptionLowerThirdDeferredErrorEvent
          ),
          tap((_) =>
            this._noticeFacade.noticeCreate(
              new NoticeCreate1Cmd({
                kind: "error",
                data: $localize`Brand caption error: ${_.roomBrandCaptionLowerThirdDeferredErrorVO.toString()}`
              })
            )
          )
        )
      ),
    { dispatch: false }
  );

  roomBrandLogoDeferredError$ = createEffect(
    () =>
      this._actions$.pipe(() =>
        this._actions$.pipe(
          ofType(RoomBrandLogoEvents.roomBrandLogoDeferredErrorEvent),
          tap((_) =>
            this._noticeFacade.noticeCreate(
              new NoticeCreate1Cmd({
                kind: "error",
                data: $localize`Brand logo error: ${_.roomBrandLogoDeferredErrorVO.toString()}`
              })
            )
          )
        )
      ),
    { dispatch: false }
  );

  roomBrandOverlayDeferredError$ = createEffect(
    () =>
      this._actions$.pipe(() =>
        this._actions$.pipe(
          ofType(RoomBrandOverlayEvents.roomBrandOverlayDeferredErrorEvent),
          tap((_) =>
            this._noticeFacade.noticeCreate(
              new NoticeCreate1Cmd({
                kind: "error",
                data: $localize`Brand overlay error: ${_.roomBrandOverlayDeferredErrorVO.toString()}`
              })
            )
          )
        )
      ),
    { dispatch: false }
  );

  roomMixerError$ = createEffect(
    () =>
      this._actions$.pipe(() =>
        this._actions$.pipe(
          ofType(RoomMixerEvents.roomMixerErrorEvent),
          tap((_) =>
            this._noticeFacade.noticeCreate(
              new NoticeCreate1Cmd({
                kind: "error",
                data: $localize`Room live stream mixer error: ${_.roomMixerErrorEventVO.value.error.toString()}`
              })
            )
          )
        )
      ),
    { dispatch: false }
  );

  roomStageLayoutDeferredError$ = createEffect(
    () =>
      this._actions$.pipe(() =>
        this._actions$.pipe(
          ofType(RoomStageLayoutEvents.roomStageLayoutDeferredErrorEvent),
          tap((_) =>
            this._noticeFacade.noticeCreate(
              new NoticeCreate1Cmd({
                kind: "error",
                data: $localize`Stage layout error: ${_.roomStageLayoutDeferredErrorVO.toString()}`
              })
            )
          )
        )
      ),
    { dispatch: false }
  );

  roomStartLiveStreamFailure$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomActions.startLiveStreamFailure),
        // In order to update state and view first
        delay(0),
        map((_) =>
          _.code === RoomError.NEXT_LIVE_TOO_EARLY && _.untilNextLiveMs
            ? $localize`Can not run live, should wait for ${ms(
                _.untilNextLiveMs,
                {
                  long: true
                }
              )}`
            : $localize`:@@roomAlerts.startLiveStreamFailure:Could not start the live stream: ${reasonOf(
                _.message
              )}:reason:. Please try again.`
        ),
        mergeMap((message) => this._confirm.alert(message))
      ),
    { dispatch: false }
  );

  roomStopLiveStreamFailure$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomActions.stopLiveStreamFailure),
        // In order to update state and view first
        delay(0),
        mergeMap((_) =>
          this._confirm.alert(
            $localize`:@@roomAlerts.stopLiveStreamFailure:Could not stop the live stream: ${reasonOf(
              _.error
            )}:reason:. Please try again.`
          )
        )
      ),
    { dispatch: false }
  );

  roomPublishScreenStreamFailure$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomActions.publishScreenStreamFailure),
        // In order to update state and view first
        delay(0),
        mergeMap((_) =>
          this._confirm.alert(
            $localize`:@@roomAlerts.publishScreenStreamFailure:Could not share your screen: ${reasonOf(
              _.error
            )}:reason:. Please try again.`
          )
        )
      ),
    { dispatch: false }
  );

  roomBrandBgCreateFailure$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomBrandBgActions.roomBrandBgCreateFailure),
        mergeMap((_) =>
          this._confirm.alert(
            $localize`:@@roomAlerts.brandBgCreateFailure:Could not upload the background image: ${reasonOf(
              _.error
            )}:reason:`
          )
        )
      ),
    { dispatch: false }
  );

  roomBrandLogoCreateFailure$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomBrandLogoActions.roomBrandLogoCreateFailure),
        mergeMap((_) =>
          this._confirm.alert(
            $localize`:@@roomAlerts.brandLogoCreateFailure:Could not upload the logo: ${reasonOf(
              _.error
            )}:reason:`
          )
        )
      ),
    { dispatch: false }
  );

  roomBrandOverlayCreateFailure$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(RoomBrandOverlayActions.roomBrandOverlayCreateFailure),
        mergeMap((_) =>
          this._confirm.alert(
            $localize`:@@roomAlerts.brandOverlayCreateFailure:Could not upload the overlay: ${reasonOf(
              _.error
            )}:reason:`
          )
        )
      ),
    { dispatch: false }
  );

  licenseLimitMaxLiveDuration$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LicenseActions.licenseLimitMaxLiveDuration),
        // In order to update state and view first
        delay(0),
        mergeMap((licenseLimitExceeded) =>
          this.room$.pipe(
            take(1),
            filter(
              (roomData) =>
                roomData?.value.id ===
                licenseLimitExceeded.licenseLimitExceededVO.value.roomId
            ),
            mergeMap(() =>
              this._confirm.alert($localize`Live duration exceeded.`)
            )
          )
        )
      ),
    { dispatch: false }
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this._actions$.pipe(
      ofType(RoomActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(this._actions$.pipe(ofType(RoomActions.destroy)))
        )
      )
    );
  }
}

/** The failure's own message, or a translated "network error" when empty. */
function reasonOf(message: string | undefined | null): string {
  return message || $localize`:@@roomAlerts.networkError:network error`;
}
